import { AiPart, PickType } from "@intelligems/ig-types";
import {
  FlattenedPart,
  MessageFlags,
  ReasoningGroup,
  ThoughtProcessGroup,
  UserAttachmentGroup,
} from "@src/features/ai/components/WorkflowEditor/execution/types";

export type VisibleItem =
  | FlattenedPart
  | ReasoningGroup
  | ThoughtProcessGroup
  | UserAttachmentGroup;

// A part that belongs in the thought-process timeline: model reasoning or a
// tool call (but NOT request-user-input, which is a visible question rendered
// as its own bubble, not a step).
function isThoughtProcessStep(item: FlattenedPart): boolean {
  if (getNodeType(item) === "reasoning") {
    return true;
  }
  if (item.part.type !== "dynamic-tool") {
    return false;
  }
  return item.part.toolName !== "request-user-input";
}

function isUserFilePart(item: FlattenedPart): boolean {
  return item.messageRole === "user" && item.part.type === "file";
}

export function isRenderableTextPart(part: PickType<AiPart, "text">): boolean {
  return !!part.text && !part.text.toLowerCase().includes("noop");
}

export function isAssistantTextBubble(item: VisibleItem): boolean {
  return (
    item.type === "part" &&
    item.messageRole === "assistant" &&
    item.part.type === "text" &&
    isRenderableTextPart(item.part)
  );
}

export function groupAndFilterNodes(
  items: FlattenedPart[],
  debug: boolean,
  showExpandedReasoningMessages?: boolean,
  reasoningNodeAllowList?: string[],
  showToolsAsSteps?: boolean
): VisibleItem[] {
  const filteredItems = filterReasoningItems(items, reasoningNodeAllowList);

  if (filteredItems.length === 0) {
    return [];
  }

  const result: VisibleItem[] = [];
  let i = 0;

  while (i < filteredItems.length) {
    const current = filteredItems[i];
    const currentType = getNodeType(current);

    // Thought-process timeline: collapse a run of consecutive reasoning + tool
    // parts into one accordion group. Each renders as a step row inside it; the
    // final assistant text bubble (the closing message) is NOT a step and stays
    // outside, ending the run.
    if (showToolsAsSteps && isThoughtProcessStep(current)) {
      const groupParts: FlattenedPart[] = [];
      let j = i;
      while (
        j < filteredItems.length &&
        isThoughtProcessStep(filteredItems[j])
      ) {
        groupParts.push(filteredItems[j]);
        j++;
      }
      const thoughtProcessGroup: ThoughtProcessGroup = {
        type: "thoughtProcessGroup",
        nodeId: `tp-${groupParts[0].nodeId}`,
        nodeName: "Thought process",
        parts: groupParts,
      };
      result.push(thoughtProcessGroup);
      i = j;
      continue;
    }

    // Group consecutive user file parts (within the same message) into a
    // single attachment row so they render as a grid instead of stacking
    // through separate Message wrappers.
    if (isUserFilePart(current)) {
      const groupParts: FlattenedPart[] = [];
      let j = i;
      while (
        j < filteredItems.length &&
        isUserFilePart(filteredItems[j]) &&
        filteredItems[j].messageId === current.messageId
      ) {
        groupParts.push(filteredItems[j]);
        j++;
      }
      // Absorb the trailing same-messageId part (the user's text bubble)
      // into the group so the renderer can place both inside one
      // `<Message>` wrapper. The executor emits file parts before the
      // text part on a single user message, so anything on the same
      // messageId after the file run is necessarily that text part. We
      // also advance past it so it isn't rendered as a separate Message.
      let textPart: FlattenedPart | undefined;
      if (
        j < filteredItems.length &&
        filteredItems[j].messageId === current.messageId
      ) {
        textPart = filteredItems[j];
        j++;
      }
      const attachmentGroup: UserAttachmentGroup = {
        type: "userAttachmentGroup",
        nodeId: current.nodeId,
        nodeName: current.nodeName,
        messageId: current.messageId,
        parts: groupParts,
        textPart,
      };
      result.push(attachmentGroup);
      i = j;
      continue;
    }

    if (currentType === "reasoning" && !showExpandedReasoningMessages) {
      const reasoningSequence = collectConsecutiveReasoning(filteredItems, i);

      if (reasoningSequence.length >= 3) {
        const sequenceWithoutPlaceholders = reasoningSequence.filter(
          (item) =>
            item.part.type === "reasoning" &&
            item.part.text !== "Thinking..." &&
            item.part.text !== "noop"
        );

        const groupParts = sequenceWithoutPlaceholders.slice(0, -1);
        const lastReasoning =
          sequenceWithoutPlaceholders[sequenceWithoutPlaceholders.length - 1];

        const reasoningGroup: ReasoningGroup = {
          type: "reasoningGroup",
          nodeId: `group-${groupParts[0].nodeId}`,
          nodeName: `Prior Thoughts (${groupParts.length} steps)`,
          parts: groupParts,
        };

        result.push(reasoningGroup);
        result.push(lastReasoning);

        i += reasoningSequence.length;
        continue;
      }
    }

    if (debug || showExpandedReasoningMessages) {
      result.push(current);
    } else if (shouldShowNode(filteredItems, i)) {
      result.push(current);
    }

    i++;
  }

  return result;
}

function filterReasoningItems(
  items: FlattenedPart[],
  reasoningNodeAllowList?: string[]
): FlattenedPart[] {
  if (!reasoningNodeAllowList?.length) {
    return items;
  }

  return items.filter((item) => {
    if (item.part.type !== "reasoning") {
      return true;
    }

    return reasoningNodeAllowList.includes(item.nodeType ?? "");
  });
}

function getNodeType(item: FlattenedPart): "reasoning" | "condition" | "other" {
  if (item.nodeType === "condition") {
    return "condition";
  }

  if (item.nodeType === "reasoning" || item.part.type === "reasoning") {
    return "reasoning";
  }

  return "other";
}

function collectConsecutiveReasoning(
  items: FlattenedPart[],
  startIndex: number
): FlattenedPart[] {
  const reasoning: FlattenedPart[] = [];
  let i = startIndex;

  while (i < items.length && getNodeType(items[i]) === "reasoning") {
    reasoning.push(items[i]);
    i++;
  }

  return reasoning;
}

function shouldShowNode(items: FlattenedPart[], index: number): boolean {
  const current = items[index];
  const currentType = getNodeType(current);
  const prev = index > 0 ? items[index - 1] : null;
  const prevType = prev ? getNodeType(prev) : null;
  const next = index < items.length - 1 ? items[index + 1] : null;
  const nextType = next ? getNodeType(next) : null;

  // Special case: Check if this reasoning is part of a group (3+ consecutive)
  if (currentType === "reasoning") {
    if (
      current.part.type === "reasoning" &&
      current.part.text === "Thinking..." &&
      nextType !== null
    ) {
      return false;
    }

    const reasoningSequence = collectConsecutiveReasoning(
      items,
      findReasoningStart(items, index)
    );
    if (reasoningSequence.length >= 3) {
      // This will be handled by the grouping logic
      return false;
    }
  }

  // Single node cases - always show
  if (!prev && !next) {
    return true;
  }

  // Reasoning visibility rules
  if (currentType === "reasoning") {
    // Check if this is the last reasoning before condition(s)
    if (nextType === "condition") {
      // Check if there are more conditions or other nodes after
      let hasNonConditionAfter = false;
      for (let i = index + 1; i < items.length; i++) {
        if (getNodeType(items[i]) !== "condition") {
          hasNonConditionAfter = true;
          break;
        }
      }

      // If only conditions follow, show the reasoning
      if (!hasNonConditionAfter) {
        return true;
      }

      // If this is the last reasoning before a chain of conditions followed by other
      // we need to check if there are more reasoning nodes after the conditions
      let nextReasoningIndex = -1;
      for (let i = index + 1; i < items.length; i++) {
        if (getNodeType(items[i]) === "reasoning") {
          nextReasoningIndex = i;
          break;
        }
      }

      // Show this reasoning if no more reasoning follows
      return nextReasoningIndex === -1;
    }

    // Generally show reasoning nodes
    return true;
  }

  // Condition visibility rules
  if (currentType === "condition") {
    // Hide conditions if followed by a non-condition node
    if (nextType && nextType !== "condition") {
      return false;
    }

    // Show if it's the last node
    if (!next) {
      return true;
    }

    // Show only the last condition in a chain
    return nextType !== "condition";
  }

  // Other nodes are generally shown
  return true;
}

function findReasoningStart(
  items: FlattenedPart[],
  currentIndex: number
): number {
  let start = currentIndex;
  while (start > 0 && getNodeType(items[start - 1]) === "reasoning") {
    start--;
  }
  return start;
}

export function shouldShowReasoningPart(
  item: FlattenedPart,
  flags: MessageFlags
) {
  if (!flags.showReasoningMessages) {
    return item.part.type !== "reasoning";
  }

  if (!flags.showConditionNodes) {
    return item.nodeType !== "condition";
  }

  return true;
}

export function getReasoningTexts(
  part: PickType<AiPart, "reasoning">,
  isCondition: boolean
) {
  let headerText = "";
  let bodyText = "";

  if (isCondition) {
    headerText = "Evaluating...";
    bodyText = part.text.trim();
  } else {
    if (part.text.startsWith("**")) {
      const endBoldIndex = part.text.indexOf("**", 2);
      if (endBoldIndex > 2) {
        // Found closing bold markers - use everything up to and including them as header
        headerText = part.text.substring(0, endBoldIndex + 2);
        bodyText = part.text.substring(endBoldIndex + 2).trim();
      } else {
        // No closing bold found - use entire text
        headerText = "**Thinking...**";
        bodyText = part.text.trim();
      }
    } else {
      // No bold markers - take first line or a reasonable snippet
      const firstLineBreak = part.text.indexOf("\n");
      if (firstLineBreak > 0) {
        headerText = part.text.substring(0, firstLineBreak);
        bodyText = part.text.substring(firstLineBreak).trim();
      } else {
        // Single line - use entire text
        headerText = part.text;
        bodyText = "";
      }
    }
  }

  return {
    headerText,
    bodyText,
  };
}
